import React, { useState, useEffect } from "react";
import { apiGet, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useForm } from "react-hook-form";
import useToastContext from "hooks/useToastContext";
import { useTranslation } from "react-i18next";
import OInputField from "components/reusable/OInputField";
import { numericInputPattern, preventMaxInput } from "utils/validations";
import formValidation from "../../utils/formValidation";
import ErrorMessage from 'components/ErrorMessage'

const StateEdit = ({ allState, currentItem, handleEdit }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [isLoading, setIsLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [cat, setCat] = useState(currentItem?.Country?._id || "");

  const {
    setValue,
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      name: currentItem?.name,
      code: currentItem?.code || currentItem?.abbreviation || "",
    },
  });

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const payload = {
        countryID: cat,
        name: data?.name?.trim(),
         code: data?.code?.trim().toUpperCase(),
      };
      var path = apiPath.stateUpdate + "/" + currentItem._id;
      const result = await apiPut(path, payload);
      if (result.data.success) {
        allState();
        handleEdit(false);
        notification.success(result?.data.message);
      } else {
        notification.error(result?.data.message);
      }
    } catch (error) {
      console.log("error in get all users list==>>>>", error);
      notification.error(error.message);
    }
    setIsLoading(false);
  };

  const handleDecimalOffer = (e) => {
    var value = e.target.value;
    setValue("stateCode", "+" + value.slice(1));
  };

  const getCountryList = async () => {
    try {
      const res = await apiGet(apiPath.getCountryList, {});
      setCountryList(res?.data?.results?.allCountries || []);
    } catch (error) {
      console.log("error fetching countries:", error);
    }
  };

  useEffect(() => {
    getCountryList();
  }, [currentItem?.Country?._id]);

  const selectCategoryCountry = async (e) => {
    const selectedCountry = e.target.value;
    if (selectedCountry) {
      setCat(selectedCountry);
      clearErrors("countryID");
    } else {
      setCat("")
      setError("countryID", {
        type: "validation",
        message: "Please select country name.",
      });
    }
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto outline-none focus:outline-none fixed inset-0 z-50 ">
        <div className="relative w-auto mx-auto max-w-3xl my-6">
          <div className="md:py-4 sm:py-8 px-7 sm:px-2">
            <div className="shadow-lg relative flex flex-col w-full border-0 rounded-lg bg-white outline-none focus:outline-none min-w-[550px]">
              <div className="justify-between p-5 border-b border-solid flex items-center border-slate-200 rounded-t">
                <h3 className="font-semibold text-xl">{t("EDIT_STATE")}</h3>
                <button
                  className=" justify-center  text-black border-2 rounded-full ml-auto flex items-center h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => handleEdit(false)}
                  title="Close"
                >
                  <span className="text-4xl text-[#B8BBBF]">×</span>
                </button>
              </div>
              {/*body*/}
              <div className="p-6 relative flex-auto">
                <div className="grid grid-cols-2">
                  {/* Country Dropdown */}
                  <div className='md:py-4 sm:px-2 sm:py-8 px-7'>
                    <div className='relative z-0 w-full group'>
                      <select
                        id='countryID'
                        value={cat}
                        onChange={selectCategoryCountry}
                        className='block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 peer'
                      >
                        <option value=''>{t('SELECT_COUNTRY')}</option>
                        {countryList.map(item => (
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                      <label
                        htmlFor='countryID'
                        className='peer-focus:font-normal absolute text-sm text-[#A5A5A5] duration-300 transform -translate-y-6 scale-75 top-3 left-3 bg-white p-2 z-10 peer-focus:left-0 peer-focus:scale-75 peer-focus:-translate-y-8'
                      >
                        {t('COUNTRY')}<span className='text-red-500'>*</span>
                      </label>
                      <ErrorMessage message={errors?.countryID?.message} />
                    </div>
                  </div>

                  <div className="md:py-4 sm:py-8 px-7 sm:px-2 ">
                    <div className="w-full group relative z-0 ">
                      <OInputField
                        id="name"
                        wrapperClassName="relative z-0 w-full group"
                        type="text"
                        name="name"
                        maxLength={30}
                        inputLabel={
                          <>
                            {t("STATE")}
                            <span className="text-red-500">*</span>
                          </>
                        }
                        register={register("name", formValidation["stateName"])}
                        placeholder=""
                        onInput={(e) => preventMaxInput(e, 30)}
                        errors={errors}
                      />
                    </div>
                  </div>

                  <div className="md:py-4 sm:py-8 px-7 sm:px-2">
                    <div className="w-full group relative z-0">
                      <OInputField
                        id="code"
                        wrapperClassName="relative z-0 w-full group"
                        type="text"
                        name="code"
                        maxLength={2}
                        inputLabel={
                          <>
                            {t("2-letter State Code")}
                            <span className="text-red-500">*</span> {/* 👈 ADDED RED ASTERISK */}
                          </>
                        }
                        register={register("code", {
                          required: "Abbreviation / Code is required", // 👈 ADDED REQUIRED VALIDATION
                          pattern: {
                            value: /^[A-Z0-9]*$/,
                            message: "Only uppercase letters and numbers allowed"
                          }
                        })}
                        placeholder=""
                        onInput={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                        }}
                        errors={errors}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t flex items-center justify-center p-6 border-solid border-slate-200 rounded-b">
                <button
                  onClick={() => handleEdit(false)}
                  className="text-black bg-[#E1E1E1] focus:outline-none rounded mr-6 font-normal px-12 py-2.5 text-sm outline-none ease-linear transition-all duration-150"
                  type="button"
                >
                  {t("O_BACK")}
                </button>
                {isLoading ? (
                  <div className="active:bg-emerald-600 spinner-container bg-LightBlue text-white font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150">
                    <div className="loading-spinner"></div>
                  </div>
                ) : (
                  <button
                    type="submit"
                    className="active:bg-emerald-600 px-8 py-2.5 font-normal text-sm bg-LightBlue text-white rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150"
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t("O_SAVE")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed inset-0 opacity-25 z-40 bg-black" />
    </>
  );
};

export default StateEdit;